import styled from "styled-components";

//#region Styles
const PrivacyPolicyWrapper = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;

  & p,
  & a,
  & tr,
  & td,
  & th,
  & ul,
  & ol,
  & li {
    color: #212b36;
    text-align: left;
    font-family: "Roboto", sans-serif;
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.55rem;

    @media only screen and (max-width: 968px) {
      font-size: 0.9rem;
      line-height: 1.4rem;
    }
  }

  & p {
    margin: 0 0 0.9rem;
  }

  & a {
    color: #1aa6e1;
    text-decoration: underline;
    overflow-wrap: anywhere;
  }

  & strong {
    font-weight: 600;
  }

  & h2 {
    color: #212b36;
    text-align: left;
    font-family: "Roboto Slab", serif;
    font-size: 1.3rem;
    font-style: normal;
    font-weight: 700;
    line-height: 1.6rem;
    margin-top: 1.6rem;
    margin-bottom: 0.6rem;

    @media only screen and (max-width: 968px) {
      font-size: 1.1rem;
      line-height: 1.4rem;
    }
  }

  & h3 {
    color: #212b36;
    text-align: left;
    font-family: "Roboto Slab", serif;
    font-size: 1.05rem;
    font-style: normal;
    font-weight: 700;
    line-height: 1.4rem;
    margin-top: 1.1rem;
    margin-bottom: 0.45rem;

    @media only screen and (max-width: 968px) {
      font-size: 1rem;
    }
  }

  & ul,
  & ol {
    margin-top: 0.25rem;
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }

  & li {
    margin-bottom: 0.45rem;
  }

  & table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 0.75rem;
    margin-bottom: 1.25rem;
    table-layout: fixed;
  }

  & th,
  & td {
    border: 1px solid #dfe3e8;
    padding: 0.75rem;
    vertical-align: top;
    overflow-wrap: anywhere;
  }

  & th {
    background-color: #f4f6f8;
    font-weight: 600;
  }

  @media only screen and (max-width: 768px) {
    & table,
    & thead,
    & tbody,
    & th,
    & td,
    & tr {
      display: block;
      width: 100%;
    }

    & thead {
      display: none;
    }

    & tr {
      margin-bottom: 0.9rem;
      border: 1px solid #dfe3e8;
    }

    & td {
      border: none;
      border-bottom: 1px solid #dfe3e8;
    }

    & td:last-child {
      border-bottom: none;
    }
  }
`;

const LabelDiv = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #1aa6e1;
  margin-bottom: 1.25rem;
  padding: 0.75rem 1.5rem;

  & h1 {
    color: #ffffff;
    text-align: left;
    font-family: "Roboto Slab", serif;
    font-size: 1.875rem;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    margin: 0.35rem 0;

    @media only screen and (max-width: 968px) {
      font-size: 1.3rem;
    }
  }

  & p {
    color: #ffffff;
    margin: 0.15rem 0 0.35rem;
    font-size: 0.9rem;
  }
`;

const NoticeBox = styled.div`
  background-color: #f4f8fb;
  border-left: 4px solid #1aa6e1;
  margin: 0.5rem 0 1rem;
  padding: 1rem;

  & p:last-child {
    margin-bottom: 0;
  }
`;
//#endregion

function PrivacyPolicyContent() {
  return (
    <PrivacyPolicyWrapper>
      <LabelDiv>
        <h1>EXTRA CASH AND CARRY PRIVACY POLICY</h1>
      </LabelDiv>

      <p>
        This Privacy Policy explains how Extra Cash and Carry
        (&quot;Extra Cash and Carry&quot;, &quot;we&quot;, &quot;our&quot; or
        &quot;us&quot;) collects, uses, stores, shares and protects personal
        information when you visit our website, create or use a customer
        account, place an order, contact us, visit our premises or otherwise
        interact with our products and services.
      </p>

      <p>
        Our website is available at{" "}
        <a
          href="https://www.extracashandcarry.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.extracashandcarry.com/
        </a>
        .
      </p>

      <p>
        We are committed to handling personal information fairly, lawfully,
        transparently and securely in accordance with applicable UK data
        protection law, including the UK General Data Protection Regulation,
        the Data Protection Act 2018 and, where relevant, the Privacy and
        Electronic Communications Regulations.
      </p>

      <NoticeBox>
        <p>
          <strong>Important:</strong> Extra Cash and Carry primarily provides
          wholesale, cash-and-carry, retail and online ordering services.
          Customer accounts may be used by sole traders, business owners,
          directors, partners, employees and authorised representatives of
          business customers.
        </p>
      </NoticeBox>

      <h2>1. Who We Are</h2>

      <p>
        For the purposes of applicable data protection law, Extra Cash and
        Carry is the controller of the personal information described in this
        Privacy Policy, unless we clearly state otherwise.
      </p>

      <p>Our contact details are:</p>

      <ul>
        <li>
          <strong>Business name:</strong> Extra Cash and Carry
        </li>

        <li>
          <strong>Address:</strong> Unit A, Brantwood Road, London, N17 0YD,
          United Kingdom
        </li>

        <li>
          <strong>Email:</strong>{" "}
          <a href="mailto:sales@extracashandcarry.com">
            sales@extracashandcarry.com
          </a>
        </li>

        <li>
          <strong>Telephone:</strong>{" "}
          <a href="tel:+442039376970">02039 376 970</a>
        </li>

        <li>
          <strong>WhatsApp online sales line:</strong>{" "}
          <a href="tel:+447494214847">07494 214 847</a>
        </li>
      </ul>

      <h2>2. Scope of This Privacy Policy</h2>

      <p>
        This Privacy Policy applies to personal information collected through:
      </p>

      <ul>
        <li>Our website and online ordering services.</li>
        <li>Customer registration, login and account management.</li>
        <li>Orders placed online, by telephone, WhatsApp, email or in person.</li>
        <li>Delivery, collection, payment and customer support processes.</li>
        <li>Enquiries, complaints, surveys and promotional communications.</li>
        <li>Visits to our shop, warehouse or other business premises.</li>
        <li>Interactions with our social media pages and advertisements.</li>
      </ul>

      <p>
        This Privacy Policy does not govern third-party websites, platforms or
        services that we do not control. Those third parties will process
        personal information under their own privacy notices.
      </p>

      <h2>3. Personal Information We Collect</h2>

      <p>
        The personal information we collect depends on how you interact with us
        and which services you use.
      </p>

      <h3>3.1 Identity and business information</h3>

      <ul>
        <li>Your full name.</li>
        <li>Your job title or role.</li>
        <li>Your business, company or trading name.</li>
        <li>Your customer or account number.</li>

        <li>
          Company registration, VAT or tax information where relevant to the
          account or transaction.
        </li>

        <li>
          Evidence that you are authorised to act for a business customer.
        </li>
      </ul>

      <h3>3.2 Contact information</h3>

      <ul>
        <li>Email address.</li>
        <li>Telephone and mobile numbers.</li>
        <li>Billing, trading, delivery and collection addresses.</li>
        <li>Preferred contact method.</li>
      </ul>

      <h3>3.3 Account and authentication information</h3>

      <ul>
        <li>Username or account email.</li>
        <li>Encrypted or securely stored authentication credentials.</li>
        <li>Login, logout and session information.</li>
        <li>Account status, permissions and customer group.</li>
        <li>Saved preferences and communication choices.</li>
      </ul>

      <h3>3.4 Order and transaction information</h3>

      <ul>
        <li>Products viewed, selected, saved or added to a basket.</li>
        <li>Orders, quantities, prices, discounts and promotional offers.</li>
        <li>Order history, invoices, credit notes and account statements.</li>
        <li>Delivery or collection instructions.</li>
        <li>Payment status and transaction references.</li>
        <li>Refunds, returns, shortages, substitutions and complaints.</li>
      </ul>

      <h3>3.5 Payment information</h3>

      <p>
        Where online or card payments are available, payment information may be
        processed by a payment service provider. We may receive limited
        information such as the payment status, payment method, transaction
        reference and the last digits of a payment card.
      </p>

      <p>
        We do not intentionally store complete payment card numbers or card
        security codes on our website systems unless this is expressly required
        and handled through an appropriately secured payment environment.
      </p>

      <h3>3.6 Communications and customer service information</h3>

      <ul>
        <li>Emails, telephone enquiries and contact-form submissions.</li>
        <li>WhatsApp or other messaging communications.</li>
        <li>Feedback, reviews, complaints and support requests.</li>
        <li>Records of actions taken to resolve an enquiry or complaint.</li>
      </ul>

      <h3>3.7 Technical and usage information</h3>

      <ul>
        <li>IP address.</li>
        <li>Browser type and version.</li>
        <li>Device type and operating system.</li>
        <li>Approximate location derived from an IP address.</li>
        <li>Website pages, products and features accessed.</li>
        <li>Referral source, date, time and duration of visits.</li>
        <li>Session identifiers and authentication information.</li>
        <li>Error, security, diagnostic and performance information.</li>
      </ul>

      <h3>3.8 Premises and security information</h3>

      <p>
        Where CCTV is used at our premises, images may be recorded for safety,
        security, crime prevention, incident investigation and the protection of
        customers, staff, visitors, stock and property.
      </p>

      <h2>4. How We Collect Personal Information</h2>

      <p>We collect personal information:</p>

      <ul>
        <li>Directly from you when you provide it to us.</li>

        <li>
          From an employer, business owner or colleague who creates or manages
          an account for you.
        </li>

        <li>
          Automatically when you use our website, subject to cookie and privacy
          settings.
        </li>

        <li>
          From payment, delivery, hosting, analytics and other service
          providers.
        </li>

        <li>
          From publicly available sources where this is lawful and relevant.
        </li>

        <li>
          From fraud prevention, credit checking or identity verification
          providers where applicable.
        </li>
      </ul>

      <h2>5. How We Use Personal Information</h2>

      <p>We may use personal information to:</p>

      <ul>
        <li>Create, verify, secure and manage customer accounts.</li>

        <li>
          Confirm that an account user is authorised to act for a business.
        </li>

        <li>
          Display products, stock availability and account-specific prices.
        </li>

        <li>Create, process, fulfil and manage orders.</li>

        <li>
          Arrange collection, delivery, substitutions, returns and refunds.
        </li>

        <li>Process and reconcile payments.</li>

        <li>Produce invoices, statements and transaction records.</li>

        <li>
          Provide customer service and respond to enquiries or complaints.
        </li>

        <li>
          Communicate important account, order and service information.
        </li>

        <li>Maintain accurate business, accounting and tax records.</li>

        <li>Manage credit limits or payment terms where applicable.</li>

        <li>
          Prevent and investigate fraud, theft, misuse and security incidents.
        </li>

        <li>
          Maintain, test, troubleshoot and improve our website and services.
        </li>

        <li>Analyse service performance and customer usage.</li>

        <li>Personalise relevant products, content and offers.</li>

        <li>Send marketing communications where permitted by law.</li>

        <li>Establish, exercise or defend legal claims.</li>

        <li>
          Comply with legal, regulatory and law-enforcement requirements.
        </li>
      </ul>

      <h2>6. Our Lawful Bases for Processing</h2>

      <p>
        We rely on one or more lawful bases depending on the purpose and
        circumstances of the processing.
      </p>

      <table>
        <thead>
          <tr>
            <th>Lawful basis</th>
            <th>How it may apply</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>Contract</td>

            <td>
              Processing needed to create or administer an account, process an
              order, receive payment, provide delivery or perform another
              requested service.
            </td>
          </tr>

          <tr>
            <td>Legal obligation</td>

            <td>
              Processing needed to comply with tax, accounting, product,
              licensing, regulatory, court or law-enforcement requirements.
            </td>
          </tr>

          <tr>
            <td>Legitimate interests</td>

            <td>
              Processing needed to operate and improve our business, protect
              customers and systems, prevent fraud, manage enquiries, maintain
              records and support relevant business marketing, provided those
              interests are not overridden by individual rights.
            </td>
          </tr>

          <tr>
            <td>Consent</td>

            <td>
              Processing carried out after you have provided a freely given,
              specific and informed choice, such as certain marketing or
              non-essential cookie activities.
            </td>
          </tr>

          <tr>
            <td>Vital interests</td>

            <td>
              Processing that is necessary in exceptional circumstances to
              protect someone&apos;s life or physical safety.
            </td>
          </tr>
        </tbody>
      </table>

      <h2>7. Business Customers and Authorised Users</h2>

      <p>
        A business customer may provide personal information about its owners,
        directors, partners, employees, delivery contacts or other authorised
        representatives.
      </p>

      <p>
        Business customers are responsible for ensuring that they are
        authorised to provide this information and that relevant individuals
        are informed about how their information may be used.
      </p>

      <h2>8. Sharing Personal Information</h2>

      <p>
        We do not sell personal information. We may share information where
        necessary with:
      </p>

      <ul>
        <li>Delivery, courier and logistics providers.</li>
        <li>Payment processors, banks and transaction service providers.</li>
        <li>Website, software, cloud, hosting and IT support providers.</li>
        <li>Email, messaging and customer communication providers.</li>
        <li>Analytics, security and fraud prevention providers.</li>
        <li>Accountants, auditors, insurers and legal advisers.</li>

        <li>
          Manufacturers or suppliers where needed to resolve a product issue.
        </li>

        <li>
          Government bodies, regulators, courts or law-enforcement authorities.
        </li>

        <li>
          A buyer, investor or adviser in connection with a business
          reorganisation, financing, sale or acquisition.
        </li>
      </ul>

      <p>
        Service providers acting on our behalf are expected to process personal
        information only for agreed purposes, apply suitable security measures
        and comply with applicable data protection requirements.
      </p>

      <h2>9. International Data Transfers</h2>

      <p>
        Some providers may store or process personal information outside the
        United Kingdom.
      </p>

      <p>
        Where required, we use recognised safeguards for international
        transfers, such as an adequacy regulation, the UK International Data
        Transfer Agreement, the UK Addendum to approved standard contractual
        clauses or another lawful transfer mechanism.
      </p>

      <h2>10. Data Security</h2>

      <p>
        We use appropriate technical and organisational measures designed to
        protect personal information against accidental or unlawful loss,
        alteration, destruction, disclosure or access.
      </p>

      <p>Measures may include:</p>

      <ul>
        <li>Role-based access controls and authentication.</li>

        <li>
          Encryption and secure network communications where appropriate.
        </li>

        <li>System monitoring, logging and security updates.</li>
        <li>Backups and business continuity arrangements.</li>
        <li>Staff confidentiality and data protection procedures.</li>

        <li>
          Supplier due diligence and contractual data protection terms.
        </li>
      </ul>

      <p>
        No website or internet transmission can be guaranteed to be completely
        secure. You are responsible for protecting your login details and
        notifying us promptly if you suspect unauthorised account access.
      </p>

      <h2>11. Data Retention</h2>

      <p>
        We keep personal information only for as long as reasonably necessary
        for the purpose for which it was collected, including to meet legal,
        tax, accounting, fraud prevention, dispute resolution and regulatory
        requirements.
      </p>

      <p>Retention periods may vary according to:</p>

      <ul>
        <li>The type and sensitivity of the information.</li>
        <li>The nature and duration of the customer relationship.</li>
        <li>Applicable limitation and record-keeping periods.</li>

        <li>
          Whether an enquiry, complaint, dispute or investigation is ongoing.
        </li>

        <li>Security and fraud prevention requirements.</li>
      </ul>

      <p>
        When information is no longer required, we will delete, anonymise or
        securely dispose of it, subject to technical and legal limitations.
      </p>

      <h2>12. Cookies and Similar Technologies</h2>

      <p>
        Our website may use cookies, local storage, session storage, pixels,
        tags and similar technologies to operate the website, maintain customer
        sessions, remember baskets and preferences, protect accounts, measure
        performance and understand website usage.
      </p>

      <p>
        Necessary technologies may be used without optional consent where they
        are essential to provide a service requested by the user. Non-essential
        technologies will be managed in accordance with applicable consent
        requirements.
      </p>

      <h3>12.1 Categories of cookies and storage</h3>

      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Purpose</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>Strictly necessary</td>

            <td>
              Used for core website functions, security, authentication,
              checkout, basket operation and requested account services.
            </td>
          </tr>

          <tr>
            <td>Preferences</td>

            <td>
              Used to remember choices such as display, language, product or
              account preferences where available.
            </td>
          </tr>

          <tr>
            <td>Analytics and performance</td>

            <td>
              Used, where enabled, to understand visits, navigation,
              performance, errors and how customers use the website.
            </td>
          </tr>

          <tr>
            <td>Marketing</td>

            <td>
              Used, where enabled and permitted, to measure campaigns or
              provide relevant advertising and promotional content.
            </td>
          </tr>
        </tbody>
      </table>

      <h3>12.2 Managing cookie choices</h3>

      <p>
        Where a cookie preference tool is available, you can use it to accept,
        reject or manage non-essential technologies. You can also change
        browser settings to block or remove cookies.
      </p>

      <p>
        Blocking strictly necessary cookies or storage may prevent login,
        basket, checkout or other website functions from working correctly.
      </p>

      <h2>13. Analytics</h2>

      <p>
        We may use analytics services to understand website traffic,
        interactions, errors and performance. Analytics information may include
        device, browser, approximate location, referral and page-view data.
      </p>

      <p>
        Where consent is required, analytics technologies should not be
        activated unless the appropriate consent has been provided.
      </p>

      <h2>14. Marketing Communications</h2>

      <p>
        We may send information about products, stock, services, promotions or
        offers where you have consented or where another lawful marketing rule
        permits us to do so.
      </p>

      <p>You may opt out of marketing at any time by:</p>

      <ul>
        <li>Using an unsubscribe link in an email.</li>

        <li>
          Changing available account or communication preferences.
        </li>

        <li>
          Emailing{" "}
          <a href="mailto:sales@extracashandcarry.com">
            sales@extracashandcarry.com
          </a>
          .
        </li>
      </ul>

      <p>
        Opting out of marketing will not prevent us from sending necessary
        account, order, payment, delivery, security or legal communications.
      </p>

      <h2>15. Social Media and Third-Party Platforms</h2>

      <p>
        When you interact with our social media pages, both we and the relevant
        platform may receive information about that interaction.
      </p>

      <p>
        Social media platforms operate under their own privacy policies. You
        should review their privacy settings and notices before sharing
        personal information.
      </p>

      <h2>16. Links to Other Websites</h2>

      <p>
        Our website may contain links to websites or services operated by third
        parties. We do not control those parties and are not responsible for
        their content, security or privacy practices.
      </p>

      <p>
        You should review the relevant third-party privacy notice before
        providing personal information.
      </p>

      <h2>17. Children&apos;s Privacy</h2>

      <p>
        Our customer accounts and online ordering services are intended for
        adults and business customers. They are not directed at children.
      </p>

      <p>
        We do not knowingly create customer accounts for children or knowingly
        collect children&apos;s personal information through our account and
        ordering services.
      </p>

      <h2>18. Automated Decisions and Profiling</h2>

      <p>
        We may use limited automated processes for fraud prevention, security,
        pricing, stock, account administration or service personalisation.
      </p>

      <p>
        We do not intend to make solely automated decisions that produce legal
        or similarly significant effects on individuals unless this is lawful
        and appropriate safeguards are provided.
      </p>

      <h2>19. Your Data Protection Rights</h2>

      <p>
        Depending on the circumstances and applicable law, you may have the
        right to:
      </p>

      <ul>
        <li>Be informed about how your personal information is used.</li>
        <li>Request access to personal information held about you.</li>
        <li>Request correction of inaccurate or incomplete information.</li>

        <li>
          Request deletion of personal information in certain circumstances.
        </li>

        <li>
          Request restriction of processing in certain circumstances.
        </li>

        <li>Object to processing based on legitimate interests.</li>
        <li>Object to direct marketing at any time.</li>
        <li>Request portability of certain information.</li>
        <li>Withdraw consent where processing is based on consent.</li>

        <li>
          Raise a concern about certain automated decisions where applicable.
        </li>
      </ul>

      <p>
        These rights are not absolute and may depend on the lawful basis,
        purpose and circumstances of the processing.
      </p>

      <h2>20. Exercising Your Rights</h2>

      <p>
        To make a data protection request, email{" "}
        <a href="mailto:sales@extracashandcarry.com">
          sales@extracashandcarry.com
        </a>{" "}
        or write to us at Unit A, Brantwood Road, London, N17 0YD, United
        Kingdom.
      </p>

      <p>
        Please describe your request clearly. We may ask for information needed
        to confirm your identity and protect personal information from
        unauthorised disclosure.
      </p>

      <p>
        We normally do not charge a fee for a valid request. A fee may be
        permitted where a request is manifestly unfounded, excessive or
        repetitive, or we may be permitted to refuse the request in limited
        circumstances.
      </p>

      <h2>21. Account Closure and Deletion Requests</h2>

      <p>
        Customers may request account closure or deletion of eligible personal
        information by contacting us.
      </p>

      <p>
        Closing an account does not necessarily require us to delete all
        related records. We may retain information required for accounting,
        tax, payment, fraud prevention, legal claims, complaints and regulatory
        compliance.
      </p>

      <h2>22. Complaints</h2>

      <p>
        Please contact us first if you have a concern about how we use personal
        information. We will investigate and try to resolve the matter.
      </p>

      <p>
        You also have the right to complain to the UK Information
        Commissioner&apos;s Office. Information about complaints is available
        at{" "}
        <a
          href="https://ico.org.uk/make-a-complaint/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://ico.org.uk/make-a-complaint/
        </a>
        .
      </p>

      <h2>23. Changes to This Privacy Policy</h2>

      <p>
        We may update this Privacy Policy to reflect changes to our business,
        website, technology, suppliers, legal requirements or data handling
        practices.
      </p>

      <p>
        The latest version will be published on this page. Where a change is
        material, we may also provide an additional notice through the website,
        customer account or another appropriate communication channel.
      </p>

      <h2>24. Contact Us</h2>

      <p>For privacy questions, requests or complaints, contact:</p>

      <ul>
        <li>
          <strong>Extra Cash and Carry</strong>
        </li>

        <li>
          Unit A, Brantwood Road, London, N17 0YD, United Kingdom
        </li>

        <li>
          Email:{" "}
          <a href="mailto:sales@extracashandcarry.com">
            sales@extracashandcarry.com
          </a>
        </li>

        <li>
          Telephone: <a href="tel:+442039376970">02039 376 970</a>
        </li>

        <li>
          Website:{" "}
          <a
            href="https://www.extracashandcarry.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.extracashandcarry.com/
          </a>
        </li>
      </ul>
    </PrivacyPolicyWrapper>
  );
}

export default PrivacyPolicyContent;